import { FormEvent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { useLanguage } from "@/i18n/LanguageContext";
import {
  fetchMyReviewAssignment,
  fetchReviewManuscriptUrl,
  saveReviewDraft,
  submitReview,
  type ConferenceReviewAssignment,
} from "./api";
import { formatDateTime } from "./format";
import { PageHeader } from "./shells/PageHeader";

const RECS = ["accept", "minor_revision", "major_revision", "reject"] as const;

export function ReviewAssignmentPage() {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const { t, locale } = useLanguage();
  const [row, setRow] = useState<ConferenceReviewAssignment | null>(null);
  const [comments, setComments] = useState("");
  const [recommendation, setRecommendation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const reload = async () => {
    if (!assignmentId) return;
    const data = await fetchMyReviewAssignment(assignmentId);
    setRow(data);
    setComments(data.review?.comments || "");
    setRecommendation(data.review?.recommendation || "");
  };

  useEffect(() => {
    if (!assignmentId) return;
    setBusy(true);
    reload()
      .catch((e) => setError(e instanceof Error ? e.message : t.common.error))
      .finally(() => setBusy(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentId, t.common.error]);

  const readOnly = row?.status === "completed" || row?.review?.status === "submitted";

  const recLabel = (value: string) => {
    switch (value) {
      case "accept":
        return t.reviews.recAccept;
      case "minor_revision":
        return t.reviews.recMinor;
      case "major_revision":
        return t.reviews.recMajor;
      case "reject":
        return t.reviews.recReject;
      default:
        return value;
    }
  };

  const onDownload = async () => {
    if (!assignmentId) return;
    setBusy(true);
    setError(null);
    try {
      const data = await fetchReviewManuscriptUrl(assignmentId);
      window.open(data.download_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  const onSaveDraft = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!assignmentId || readOnly) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await saveReviewDraft(assignmentId, {
        comments,
        recommendation: recommendation || null,
      });
      setMessage(t.reviews.draftSaved);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = async () => {
    if (!assignmentId || readOnly) return;
    if (!comments.trim() || !recommendation) {
      setError(t.reviews.submitNeedFields);
      return;
    }
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await submitReview(assignmentId, { comments, recommendation });
      setMessage(t.reviews.submitted);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.common.error);
    } finally {
      setBusy(false);
    }
  };

  if (!row && !error) return <p>{t.common.loading}</p>;
  if (!row) {
    return (
      <div>
        <PageHeader title={t.reviews.reviewTitle} backTo="/reviews" backLabel={t.reviews.title} />
        <div className="dn-alert dn-alert-error">{error || t.common.error}</div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={t.reviews.reviewTitle} backTo="/reviews" backLabel={t.reviews.title} />

      <div className="dn-card" style={{ marginBottom: "1.25rem" }}>
        <h3>{row.title}</h3>
        <p>
          <strong>{t.reviews.colAuthors}:</strong> {row.authors}
          <br />
          <strong>{t.reviews.colLanguage}:</strong> {row.language.toUpperCase()}
          <br />
          <strong>{t.reviews.colAssigned}:</strong> {formatDateTime(row.assigned_at, locale)}
          <br />
          <strong>{t.reviews.colDue}:</strong>{" "}
          {row.due_at ? formatDateTime(row.due_at, locale) : "—"}
          <br />
          <strong>{t.reviews.colStatus}:</strong>{" "}
          {row.status === "completed" ? t.reviews.statusCompleted : t.reviews.statusAssigned}
        </p>
        {row.keywords && (
          <p>
            <strong>{t.paper.keywords}:</strong> {row.keywords}
          </p>
        )}
        {row.abstract && (
          <p>
            <strong>{t.paper.abstract}:</strong> {row.abstract}
          </p>
        )}
        <button
          type="button"
          className="dn-btn dn-btn-ghost"
          disabled={busy || !row.has_manuscript}
          onClick={onDownload}
        >
          {row.has_manuscript ? t.reviews.downloadManuscript : t.reviews.noManuscript}
        </button>
      </div>

      {error && <div className="dn-alert dn-alert-error">{error}</div>}
      {message && (
        <div className="dn-alert" style={{ background: "rgba(15,79,58,0.08)" }}>
          {message}
        </div>
      )}
      {readOnly && (
        <div className="dn-alert" style={{ background: "rgba(15,79,58,0.08)" }}>
          {t.reviews.readOnlyHelp}
        </div>
      )}

      <form className="dn-card" onSubmit={onSaveDraft}>
        <h3>{t.reviews.formTitle}</h3>
        <label style={{ display: "block", marginBottom: "0.75rem" }}>
          {t.reviews.comments}
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={8}
            disabled={readOnly || busy}
            style={{ display: "block", width: "100%", marginTop: 6 }}
            required={!readOnly}
          />
        </label>
        <label style={{ display: "block", marginBottom: "1rem" }}>
          {t.reviews.recommendation}
          <select
            value={recommendation}
            onChange={(e) => setRecommendation(e.target.value)}
            disabled={readOnly || busy}
            style={{ display: "block", marginTop: 6, minWidth: 260 }}
            required={!readOnly}
          >
            <option value="">{t.reviews.recommendationPlaceholder}</option>
            {RECS.map((r) => (
              <option key={r} value={r}>
                {recLabel(r)}
              </option>
            ))}
          </select>
        </label>
        {row.review?.submitted_at && (
          <p className="dn-muted">
            {t.reviews.submittedAt}: {formatDateTime(row.review.submitted_at, locale)}
          </p>
        )}
        {!readOnly && (
          <div className="dn-cta-row" style={{ flexWrap: "wrap" }}>
            <button type="submit" className="dn-btn dn-btn-ghost" disabled={busy}>
              {t.reviews.saveDraft}
            </button>
            <button type="button" className="dn-btn dn-btn-primary" disabled={busy} onClick={onSubmit}>
              {t.reviews.submitFinal}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
